import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import api from "../lib/axios";
import type { RootState } from "../store";
import { hasSubscriptionCheckTimedOut } from "../lib/checkSubscriptionTimeout";
import type { 
  SubscriptionState, 
  // PackageInfo, 
  // PackagePermissions, 
  // PackageStyle, 
  SubscriptionPackage, 
  UsageOverview} from "../types/subscription";
const LOCAL_KEY = "subscription_state_v1";

const loadFromLocal = (): SubscriptionState | undefined => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? (JSON.parse(raw) as SubscriptionState) : undefined;
  } catch {
    return undefined;
  }
};

const saveToLocal = (state: SubscriptionState) => {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(state));
  } catch (err) {
    // ignore
  }
};

const initialFromLocal = loadFromLocal();

// If we already know a fetch will be required (no prior check or cache expired),
// start with loading: true so layouts show a spinner on the very first render
// instead of briefly flashing the "no active plan" modal before the effect fires.
const needsImmediateCheck =
  !initialFromLocal?.lastCheckedAt ||
  hasSubscriptionCheckTimedOut(initialFromLocal.lastCheckedAt as string);

const initialState: SubscriptionState = {
  ...(initialFromLocal ?? {
    error: null,
    active: false,
    trial: false,
    subscription: null,
    usageOverview: null,
    permissions: null,
    style: null,
    lastCheckedAt: null,
    packages: [],
    packagesError: null,
    selectedPackage: null,
    hasSubscription: false,
    expired: false,
  }),
  loading: needsImmediateCheck,
};


//--------------------------------------------------
// THUNKS
//--------------------------------------------------

// // Async thunk to fetch subscription
// export const fetchSubscription = createAsyncThunk<
//     { subscription: PackageInfo | null; permissions: PackagePermissions | null; style: PackageStyle | null; active: boolean; trial: boolean },
//     null,
//     { rejectValue: string }
//     >("subscription/current", async (_, { rejectWithValue }) => {
//     try {
//         const res = await api.get(`/subscription/current`);
//         // expect server response already in desired shape:
//         // { active, trial, subscription, permissions, style }
//         return {
//             subscription: res.data.subscription ?? null,
//             permissions: res.data.permissions ?? null,
//             style: res.data.style ?? null,
//             active: !!res.data.active,
//             trial: !!res.data.trial,
//         };
//     } catch (err: any) {
//         const msg =
//         err?.response?.data?.message || err?.message || "Failed to fetch subscription";
//         return rejectWithValue(msg);
//     }
// });

// Fetch all packages for plans page
export const fetchPackages = createAsyncThunk<
  SubscriptionPackage[], // response is an array of packages
  null,
  { rejectValue: string }
>("subscription/packages", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/subscription/packages");
    // console.log("PACKAGES RESPONSE:", res.data);
    return res.data.data as SubscriptionPackage[]; // expecting array from backend
  } catch (err: any) {
    const msg = err?.response?.data?.message || err?.message || "Failed to fetch packages";
    console.log("PACKAGES ERROR:", err.response?.data || err);
    return rejectWithValue(msg);
  }
});

// Record user subscription details
export const startSubscription = createAsyncThunk<
  any,
  {
    selectedPackage: SubscriptionPackage,
    payload: any
  },
  { rejectValue: string }
>("subscription/start", async (payload, { rejectWithValue }) => {
  try {
    // const { package_id, billing_cycle, auto_renew, freeTrial } = payload;
    const res = await api.post(
      `/subscription/select?free_trial=${payload.payload.freeTrial ? "true" : "false"}`,
      {
        package_id: payload.payload.package_id,
        billing_cycle: payload.payload.billing_cycle,
        auto_renew: payload.payload.auto_renew,
        amount: payload.payload.amount
      }
    );

    console.log("Response: ", res.data);
    console.log("Selected package: ", payload.selectedPackage);

    
    return {
        selectedPackage: payload.selectedPackage,            // ✅ store the package user selected
        subscription: res.data.subscription // ✅ store the created subscription
    };
  } catch (err: any) {
    const msg =
      err?.response?.data?.message || err?.message || "Failed to start subscription";
    return rejectWithValue(msg);
  }
});

// Renew current subscription after payment
export const renewSubscription = createAsyncThunk<
  any,
  { amount: number | string },
  { rejectValue: string }
>("subscription/renew", async (payload, { rejectWithValue }) => {
  try {
    const res = await api.post("/subscription/renew", {
      amount: payload.amount,
    });
    return res.data.subscription;
  } catch (err: any) {
    const msg =
      err?.response?.data?.message || err?.message || "Failed to renew subscription";
    return rejectWithValue(msg);
  }
});

// Fetch subscription usage overview
export const fetchSubscriptionUsageOverview = createAsyncThunk<
  UsageOverview,
  void,
  { rejectValue: string }
>("subscription/usage", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get("/subscription/usage");
    return res.data.data as UsageOverview;
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to fetch usage overview")
  }
});


// export const checkSubscriptionStatus = createAsyncThunk<
//   { expired: boolean; active: boolean; hasSubscription: boolean },
//   void,
//   { state: RootState; rejectValue: string }
// >("subscription/checkStatus", async (_, { getState, rejectWithValue }) => {
//   try {
//     const { subscription } = getState().subscription;

//     // ✅ Case 1: No subscription at all
//     if (!subscription) {
//       return { expired: false, active: false, hasSubscription: false };
//     }

//     // ✅ Case 2: Subscription exists but has no expiry date (active)
//     if (!subscription.endDate) {
//       return { expired: false, active: true, hasSubscription: true };
//     }

//     // ✅ Case 3: Subscription expired or still valid
//     const expiryDate = new Date(subscription.endDate);
//     const now = new Date();

//     const expired = now > expiryDate;
//     return { expired, active: !expired, hasSubscription: true };
//   } catch (err: any) {
//     const msg = err?.message || "Failed to check subscription status";
//     return rejectWithValue(msg);
//   }
// });

export const checkSubscriptionStatus = createAsyncThunk<
  {
    expired: boolean;
    active: boolean;
    hasSubscription: boolean;
    trial: boolean;
    subscription: any | null;
    permissions: any | null;
    style: any | null;
  },
  void,
  { state: RootState; rejectValue: string }
>(
  "subscription/checkStatus",
  async (_, { rejectWithValue }) => {
    try {
      // 👇 Call your backend controller
      const { data } = await api.get("/subscription/current", {
        withCredentials: true,
      });


      const { active, trial, subscription, permissions, style } = data;

      // Case 1: No subscription
      if (!subscription) {
        return {
          expired: false,
          active: false,
          hasSubscription: false,
          trial,
          subscription: null,
          permissions: null,
          style: null,
        };
      }

      // Case 2: Check expiry
      const now = new Date();
      const expiryDate = subscription.endDate ? new Date(subscription.endDate) : null;
      const expired = expiryDate ? now > expiryDate : false;

      return {
        expired,
        active,
        hasSubscription: true,
        trial,
        subscription,
        permissions,
        style,
      };
    } catch (err: any) {
      const msg =
        err.response?.data?.message ||
        err.message ||
        "Failed to check subscription status";
      return rejectWithValue(msg);
    }
  }
);



//--------------------------------------------------
// SLICE
//--------------------------------------------------


const slice = createSlice({
  name: "subscription",
  initialState,
  reducers: {
    setSubscription(state, action: PayloadAction<Partial<SubscriptionState>>) {
      Object.assign(state, action.payload);
      saveToLocal(state);
    },
    clearSubscription(state) {
      state.active = false;
      state.trial = false;
      state.subscription = null;
      state.permissions = null;
      state.style = null;
      state.error = null;
      state.lastCheckedAt = null;
      saveToLocal(state);
    },
  },
  extraReducers: (builder) => {
    builder
      // .addCase(fetchSubscription.pending, (state) => {
      //   state.loading = true;
      //   console.log("Fetching subscription");
      //   state.error = null;
      // })
      // .addCase(fetchSubscription.fulfilled, (state, { payload }) => {
      //   state.loading = false;
      //   state.error = null;
      //   state.subscription = payload.subscription;
      //   state.permissions = payload.permissions;
      //   state.style = payload.style;
      //   state.active = payload.active;
      //   state.trial = payload.trial;
      //   state.lastCheckedAt = new Date().toISOString();
      //   saveToLocal(state);
      // })
      // .addCase(fetchSubscription.rejected, (state, _) => {
      //   state.loading = false;
      //   // state.error = payload ?? "Failed to fetch subscription";
      //   // keep existing subscription in state (don't clear)
      // })

      // ####### FETCH PACKAGES
      .addCase(fetchPackages.pending, (state) => {
        state.loading = true;
        state.packagesError = null;
      })
     .addCase(fetchPackages.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.packages = payload;

        if (!state.selectedPackage) return;

        const updatedPackage = payload.find(
          (pkg) => pkg.id === state.selectedPackage!.id
        );

        if (!updatedPackage) {
          // Package no longer exists
          state.selectedPackage = null;
          state.permissions = null;
          return;
        }

        state.selectedPackage = updatedPackage;

        // Update permissions from latest package details
        state.permissions = updatedPackage.PackageDetails?.[0] ?? null;
      })
      .addCase(fetchPackages.rejected, (state, { payload }) => {
        state.loading = false;
        state.packagesError = payload ?? "Failed to load plans";
      })

      // ####### START SUBSCRIPTION
       .addCase(startSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startSubscription.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPackage = action.payload.selectedPackage;   // save package
        state.subscription = action.payload.subscription;         // save subscription
        state.permissions = action.payload.selectedPackage.PackageDetails[0];
        state.style = action.payload.selectedPackage.PackageStyles[0];
        state.expired = false;
        state.hasSubscription = true;
      })
      .addCase(startSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ####### CHECK SUBSCRIPTION STATUS
      // .addCase(checkSubscriptionStatus.fulfilled, (state, { payload }) => {
      //   state.expired = payload.expired;
      //   state.active = payload.active;
      //   state.hasSubscription = payload.hasSubscription;
      //   saveToLocal(state);
      // })
      // .addCase(checkSubscriptionStatus.rejected, (state, action) => {
      //   state.error = action.payload ?? "Failed to check subscription status";
      // })
      .addCase(checkSubscriptionStatus.pending, (state) => {
        state.loading = true;
        console.log("Subscription loading: ", state.loading);
        state.error = null;
      })
      .addCase(checkSubscriptionStatus.fulfilled, (state, { payload }) => {
        state.loading = false;
        console.log("Subscription loading after fulfillment: ", state.loading);
        state.expired = payload.expired;
        state.active = payload.active;
        state.hasSubscription = payload.hasSubscription;
        state.trial = payload.trial;
        state.subscription = payload.subscription;
        state.permissions = payload.permissions;
        state.style = payload.style;
        state.lastCheckedAt = new Date().toISOString();
      })
      .addCase(checkSubscriptionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? "Failed to check subscription status";
      })


      // ####### RENEW SUBSCRIPTION
      .addCase(renewSubscription.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(renewSubscription.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.subscription = payload;
        state.expired = false;
        state.active = true;
        state.hasSubscription = true;
        saveToLocal(state);
      })
      .addCase(renewSubscription.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ####### FETCH USAGE OVERVIEW
      .addCase(fetchSubscriptionUsageOverview.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchSubscriptionUsageOverview.fulfilled, (state, action) => {
        state.usageOverview = action.payload;
        state.error = null;
      })
      .addCase(fetchSubscriptionUsageOverview.rejected, (state, action) => {
        state.error = action.payload;
      })
  },
});

export const { setSubscription, clearSubscription } = slice.actions;

export const subscriptionReducer = slice.reducer;

// selector helpers
export const selectSubscriptionState = (state: RootState) => state.subscription;
export const selectIsActive = (state: RootState) => state.subscription.active;
export const selectIsTrial = (state: RootState) => state.subscription.trial;
export const selectPermissions = (state: RootState) => state.subscription.permissions;
export const selectStyle = (state: RootState) => state.subscription.style;
export const selectSubscriptionLoading = (state: RootState) => state.subscription.loading;
export const selectSubscriptionError = (state: RootState) => state.subscription.error;
export const selectPackages = (state: RootState) => state.subscription.packages;
export const selectPackagesError = (state: RootState) => state.subscription.packagesError;